import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import HelmetWrapper from 'components/HelmetWrapper';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import useMultiSort from 'hooks/useMultiSort';
import MultiSortPanel from 'components/MultiSortPanel';
import MultiFilterPanel from 'components/MultiFilterPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import { DATA_ROOM_SHAREABLE_LINK_ACCESS } from 'models/ShareableLink';
import {
  WaitingRoomRequest,
  getIsBuySideInviteRequest,
  getIsReviewableRequest,
} from 'models/WaitingRoom';
import { SideDrawerRef } from '../../components/SideDrawer/SideDrawer.component';
import { DataRoomMember } from '../../models/DataRoomMember';
import { ManageRequests } from '../../subrouters/Members/pages/Members/components/ManageRequests';
import CreateShareableLinkProvider from '../../providers/CreateShareableLink';
import CreateShareableLinkContext from '../../providers/CreateShareableLink/CreateShareableLink.context';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import MembersContext from '../../providers/Members/Members.context';
import OnboardingContext from '../../providers/Onboarding/Onboarding.context';
import {
  useInvalidateWaitingRoom,
  useWaitingRoomCount,
  useWaitingRoomQuery,
} from '../../hooks/useWaitingRoom';
import useWaitingRoomParams from './hooks/useWaitingRoomParams';
import useWaitingRoomFilters from './hooks/useWaitingRoomFilters';
import {
  MULTI_SORT_DIRECTION_COPY,
  MULTI_SORT_LABELS,
  SORTING_VALUES,
  WAITING_ROOM_PAGE_PARAM,
  WAITING_ROOM_SORT_COLUMNS,
  WAITING_ROOM_SORT_PARAM,
  WAITING_ROOM_FILTER_PARAMS,
} from './components/WaitingRoomTable/data';
import WaitingRoomTable from './components/WaitingRoomTable';
import WaitingRoomEmptyState from './components/WaitingRoomEmptyState';
import { ReviewSecureLinkRequest } from './components/ReviewSecureLinkRequest';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import { getFilterOptionByValue } from 'utils';
import useDrawerNavigation from 'hooks/useDrawerNavigation';

import './WaitingRoom.styles.scss';

type WaitingRoomProps = {
  className?: string;
};

// Fallback for a request whose member the provider has not caught up with yet.
// The queue payload carries no roles, so the drawer's selects open empty — the
// member from `MembersContext` is what prefills them.
const getRequestMember = ({
  dataRoomMemberId,
  userId,
  firstName,
  lastName,
  email,
}: WaitingRoomRequest) =>
  ({
    _id: dataRoomMemberId,
    userId,
    firstName,
    lastName,
    email,
  } as DataRoomMember);

const WaitingRoomContent: React.FC<WaitingRoomProps> = (props) => {
  const { className } = props;

  const {
    dataRoom: { dealRoom },
  } = useContext(DataRoomContext);

  const { contentCard } = useContext(OnboardingContext);

  const { allMembers } = useContext(MembersContext);

  const { openCreateShareableLinkWithTemplate } = useContext(
    CreateShareableLinkContext,
  );

  const { apiParams, sort, updateSort } = useWaitingRoomParams();

  const { requests, totalPages, filteredCount, isLoading, isFetching } =
    useWaitingRoomQuery(apiParams);

  // The header line and the empty state both describe the queue as a whole, so
  // they read the unfiltered total rather than what is on screen.
  const totalRequestsCount = useWaitingRoomCount();

  const classes = classNames('waiting-room', 'page', className);

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const filterOptions = useWaitingRoomFilters();

  // Same sorter the members tables use, over the queue's own sortable columns.
  // The BE takes several fields at once, so the modal's order is honoured as-is.
  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    WAITING_ROOM_SORT_COLUMNS,
    SORTING_VALUES,
    MULTI_SORT_LABELS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(
    () => ({
      requestor: {
        options: getFilterOptionByValue(
          filterOptions,
          WAITING_ROOM_FILTER_PARAMS.REQUESTORS,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.requestor],
      },
      email: {
        options: getFilterOptionByValue(
          filterOptions,
          WAITING_ROOM_FILTER_PARAMS.EMAILS,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.email],
      },
      type: {
        options: getFilterOptionByValue(
          filterOptions,
          WAITING_ROOM_FILTER_PARAMS.TYPES,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.type],
      },
      requestingAccessTo: {
        options: getFilterOptionByValue(
          filterOptions,
          WAITING_ROOM_FILTER_PARAMS.REQUESTING_ACCESS_TO,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.requestingAccessTo],
      },
      requested: {
        options: getFilterOptionByValue(
          filterOptions,
          WAITING_ROOM_FILTER_PARAMS.REQUESTED,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.requested],
      },
    }),
    [filterOptions, multiSortDirectionTranslation],
  );

  const [selectedMember, setSelectedMember] = useState<DataRoomMember>();
  const [selectedRequest, setSelectedRequest] = useState<WaitingRoomRequest>();
  const [selectedRequestId, setSelectedRequestId] = useState<string>();

  const [manageRequests, manageRequestsRef] =
    useCallbackRef<SideDrawerRef>(null);
  const [reviewSecureLink, reviewSecureLinkRef] =
    useCallbackRef<SideDrawerRef>(null);

  const reviewBuySideInvite = useCallback(
    (request: WaitingRoomRequest) => {
      // The drawer prefills its role selects off the member, and only the
      // provider's copy carries the roles the invite was created with.
      const member = allMembers.find(
        ({ _id }) => _id === request.dataRoomMemberId,
      );

      setSelectedMember(member ?? getRequestMember(request));

      reviewSecureLink?.setOpen(false);
      manageRequests?.setOpen(true);
    },
    [allMembers, manageRequests, reviewSecureLink],
  );

  // The two sides of the queue are settled in different places — a buy-side
  // invite through the member it created, a secure link request through the link
  // it came in on — so each opens its own drawer.
  const onReview = useCallback(
    (request: WaitingRoomRequest) => {
      if (!getIsReviewableRequest(request)) return;

      setSelectedRequestId(request._id);

      if (getIsBuySideInviteRequest(request))
        return reviewBuySideInvite(request);

      setSelectedRequest(request);

      manageRequests?.setOpen(false);
      reviewSecureLink?.setOpen(true);
    },
    [manageRequests, reviewBuySideInvite, reviewSecureLink],
  );

  const { onPrev, onNext } = useDrawerNavigation({
    rows: requests,
    selectedId: selectedRequestId,
    totalPages,
    pageParam: WAITING_ROOM_PAGE_PARAM,
    isLoading: isFetching,
    getIsNavigable: getIsReviewableRequest,
    onNavigate: onReview,
  });

  // Everything about the link carries over, so the seller is adjusting a copy
  // rather than building from scratch. The new link exists for one person, so it
  // is locked to them whatever the original allowed. The queue row's id rides
  // along as `waitingRoomRequestId`: creating the link is what settles the
  // request, and that is how the BE knows which row to clear.
  //
  // Nothing is settled until the link is created, so the review the seller came
  // from is still theirs to go back to: that row's id is what has the create
  // drawer offer the way back, and the request is still in state for the review
  // drawer to reopen on.
  const onCreateNewLink = useCallback(
    ({ _id, email, dataRoomShareableLink }: WaitingRoomRequest) =>
      openCreateShareableLinkWithTemplate?.(
        {
          ...dataRoomShareableLink,
          linkAccess: DATA_ROOM_SHAREABLE_LINK_ACCESS.SPECIFIC_RECIPIENTS,
          recipientEmailAddresses: [email],
          waitingRoomRequestId: _id,
        },
        () => reviewSecureLink?.setOpen(true),
      ),
    [openCreateShareableLinkWithTemplate, reviewSecureLink],
  );

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('waitingRoom')} - ${dealRoom?.name}`} />

      <div className="waiting-room__header">
        <h2>{t('waitingRoom')}</h2>

        <p className="waiting-room__header__subtitle">
          {t('waitingRoomSubtitle')}
        </p>
      </div>

      {contentCard}

      {isLoading ? (
        <Loader />
      ) : totalRequestsCount ? (
        <>
          <div className="waiting-room__toolbar">
            <ActiveFilterSortPills
              filterOptions={filterOptions}
              sortOptions={multiSortValues}
              sortParam={WAITING_ROOM_SORT_PARAM}
              onChipLabelClick={onChipLabelClick}
              onSortChipLabelClick={onSortChipLabelClick}
            />

            <div className="waiting-room__toolbar__actions">
              <MultiFilterPanel
                ref={multiFilterRef}
                filters={filterOptions}
                pageParam={WAITING_ROOM_PAGE_PARAM}
              />

              <MultiSortPanel
                ref={multiSortRef}
                options={multiSortValues}
                sortDirectionTranslations={multiSortDirectionTranslation}
                sortParam={WAITING_ROOM_SORT_PARAM}
              />
            </div>
          </div>

          <WaitingRoomTable
            requests={requests}
            sort={sort}
            totalPages={totalPages}
            totalItems={filteredCount}
            isLoading={isFetching}
            updateSort={updateSort}
            onReview={onReview}
            columnFilterOptions={columnFilterOptions}
          />
        </>
      ) : (
        <WaitingRoomEmptyState />
      )}

      {/* Approving and declining both refetch the queue from inside the drawer,
      so nothing is passed to sync: the reviewed member stays in state until the
      next review, keeping the drawer's title through the closing animation. */}
      <ManageRequests
        ref={manageRequestsRef}
        isBuySide
        member={selectedMember}
        onPrev={onPrev}
        onNext={onNext}
      />

      <ReviewSecureLinkRequest
        ref={reviewSecureLinkRef}
        request={selectedRequest}
        onCreateNewLink={onCreateNewLink}
        onPrev={onPrev}
        onNext={onNext}
      />
    </div>
  );
};

// The create-secure-link drawer is the other way out of a secure link request, so
// the queue is wrapped in the provider that owns it. Creating the link resolves
// the request on the BE, which is why success refetches the queue.
const WaitingRoom: React.FC<WaitingRoomProps> = (props) => {
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const invalidateWaitingRoom = useInvalidateWaitingRoom(dataRoomId);

  return (
    <CreateShareableLinkProvider onSuccessFullyCreated={invalidateWaitingRoom}>
      <WaitingRoomContent {...props} />
    </CreateShareableLinkProvider>
  );
};

export default WaitingRoom;
